import { UploadOutlined } from "@ant-design/icons";
import { Alert, Space, Table, Typography } from "antd";
import type { ColumnsType } from "antd/es/table";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import type { RequestStatus } from "@/entities/deposit";
import type { AdminWithdrawal } from "@/entities/withdrawal";
import { useWithdrawals } from "@/entities/withdrawal";
import { WithdrawalActions } from "@/features/process-withdrawal";
import { errorMessage } from "@/shared/api";
import { SECTIONS } from "@/shared/config";
import { formatDateTime } from "@/shared/lib";
import {
  EmptyState,
  LoadMore,
  Money,
  PageHeader,
  SectionCard,
  StatusTag,
  UserCell,
} from "@/shared/ui";
import { StatusFilter } from "@/widgets/request-table";

const STATUSES: RequestStatus[] = ["pending", "approved", "paid", "rejected"];

export function WithdrawalsPage() {
  const { t } = useTranslation();
  const [statuses, setStatuses] = useState<RequestStatus[]>(["pending"]);
  const query = useWithdrawals(statuses);

  const rows = query.data?.pages.flatMap((page) => page.items) ?? [];

  /**
   * Колонки сжаты попарно — сеть с адресом и статус с датой. Причина та же,
   * что на пополнениях: после укрупнения шрифта девять колонок не влезали, и
   * действия справа закрывали статус.
   *
   * Порядок тот же, что на пополнениях: статус сразу за суммой, реквизиты и
   * хеш уезжают вправо.
   */
  const columns: ColumnsType<AdminWithdrawal> = [
    { title: t("common.id"), dataIndex: "id", width: 84 },
    {
      title: t("common.user"),
      key: "user",
      render: (_, row) => (
        <UserCell
          id={row.user.id}
          username={row.user.username}
          firstName={row.user.first_name}
          telegramId={row.user.telegram_id}
        />
      ),
    },
    {
      title: t("common.amount"),
      key: "amount",
      align: "right",
      width: 170,
      render: (_, row) => <Money value={row.amount} />,
    },
    {
      title: t("common.status"),
      key: "status",
      width: 210,
      render: (_, row) => (
        <Space orientation="vertical" size={4} align="start">
          <StatusTag status={row.status} />
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            {formatDateTime(row.created_at)}
          </Typography.Text>
        </Space>
      ),
    },
    {
      title: t("common.requisites"),
      key: "requisites",
      width: 240,
      render: (_, row) => (
        <Space orientation="vertical" size={0} style={{ maxWidth: 210 }}>
          <span>{`${row.asset} · ${row.network}`}</span>
          {/* Адрес выплаты копируется целиком: по нему оператор отправляет
              деньги руками, и опечатка здесь — потерянные деньги. */}
          <Typography.Text copyable ellipsis type="secondary" style={{ fontSize: 12 }}>
            {row.address}
          </Typography.Text>
        </Space>
      ),
    },
    {
      title: t("withdrawals.txHash"),
      key: "tx_hash",
      width: 190,
      render: (_, row) =>
        row.tx_hash ? (
          <Typography.Text copyable ellipsis style={{ maxWidth: 150 }}>
            {row.tx_hash}
          </Typography.Text>
        ) : (
          <Typography.Text type="secondary">—</Typography.Text>
        ),
    },
    {
      title: t("common.actions"),
      key: "actions",
      // Не закреплена — по той же причине, что и на пополнениях: закреплённая
      // колонка перекрывала статус на узком экране.
      width: 430,
      render: (_, row) => (
        <WithdrawalActions
          id={row.id}
          userId={row.user.id}
          amount={row.amount}
          status={row.status}
        />
      ),
    },
  ];

  return (
    <>
      <PageHeader
        section="withdrawals"
        title={t("withdrawals.title")}
        subtitle={t("withdrawals.subtitle")}
        extra={<StatusFilter value={statuses} onChange={setStatuses} statuses={STATUSES} />}
      />

      {query.isError && (
        <Alert type="error" showIcon title={errorMessage(query.error)} style={{ marginBottom: 16 }} />
      )}

      <SectionCard color={SECTIONS.withdrawals.color} bodyPadding={0}>
        <Table<AdminWithdrawal>
          rowKey="id"
          columns={columns}
          dataSource={rows}
          loading={query.isPending}
          pagination={false}
          scroll={{ x: 1560 }}
          locale={{
            emptyText: <EmptyState icon={<UploadOutlined aria-hidden />} title={t("withdrawals.empty")} />,
          }}
        />
        <LoadMore
          hasMore={query.hasNextPage}
          loading={query.isFetchingNextPage}
          onClick={() => {
            void query.fetchNextPage();
          }}
        />
      </SectionCard>
    </>
  );
}
